import { Link, useParams } from 'react-router-dom';
import EmptyState from '@/components/base/EmptyState';
import { useSeo } from '@/hooks/useSeo';
import { newsList } from '@/mocks/news';
import { formatDate } from '@/utils/media';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);
  const news = newsList.find((n) => n.id === numericId);

  useSeo({
    title: news ? `${news.title} - 天堂影视资讯` : '资讯不存在 - 天堂影视',
    description: news
      ? news.summary
      : '未找到对应的资讯内容，请返回天堂影视资讯频道浏览更多影视动态。',
  });

  if (!news) {
    return (
      <div className="container-page py-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="资讯不存在"
          description="你访问的资讯可能已被删除或链接有误，返回资讯列表看看其他内容吧。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/news"
            className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  const related = news.relatedIds
    .map((rid) => newsList.find((n) => n.id === rid))
    .filter((n): n is NonNullable<typeof n> => Boolean(n));

  return (
    <div className="container-page py-6">
      <nav className="flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/news" className="transition-colors hover:text-primary-600 cursor-pointer">
          影视资讯
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="truncate text-foreground-800">{news.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
        <article className="min-w-0">
          <span className="inline-block rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
            {news.category}
          </span>
          <h1 className="mt-3 text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {news.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 border-b border-background-200 pb-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {formatDate(news.date)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-newspaper-line"></i>
              来源：{news.source}
            </span>
          </div>

          <div className="mt-6 overflow-hidden rounded-lg">
            <img
              src={news.thumbnail}
              alt={news.title}
              title={`${news.title} - 天堂影视资讯配图`}
              className="h-56 w-full object-top md:h-80"
            />
          </div>

          <div className="mt-6 space-y-4">
            {news.content.map((paragraph, i) => (
              <p key={i} className="text-sm leading-loose text-foreground-700 md:text-base">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-background-200 pt-6">
            <Link
              to="/news"
              className="flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回资讯列表
            </Link>
            <p className="hidden text-xs text-foreground-400 sm:block">
              本文为演示内容，不代表真实报道。
            </p>
          </div>
        </article>

        <aside>
          <div className="rounded-lg border border-background-200 bg-background-100 p-4">
            <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                <i className="ri-links-line"></i>
              </span>
              相关文章
            </h2>
            <ul className="mt-4 space-y-4">
              {related.map((rel) => (
                <li key={rel.id}>
                  <Link to={`/news/${rel.id}`} className="group flex gap-3 cursor-pointer">
                    <span className="h-14 w-20 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={rel.thumbnail}
                        alt={rel.title}
                        title={`${rel.title} - 天堂影视资讯`}
                        className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                        {rel.title}
                      </span>
                      <span className="mt-1 block text-xs text-foreground-400">
                        {rel.date}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}